import { MutationSystem } from "@/lib/queuedMutations";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import type {
  Meeting,
  Team,
  Attendee,
  User,
  AgendaItem,
  AgendaModule,
  AgendaItemPosition,
  AgendaItemTemplate,
} from "@repo/types";
import { firstValueFrom } from "rxjs";

// get users that are present and can vote
export const getVoters = ({
  meeting,
  team,
  userMap,
}: {
  meeting: Meeting;
  team: Team;
  userMap: Record<string, User>;
}): { all: User[]; present: User[]; absent: User[] } => {
  const userPresence =
    meeting.review?.attendees?.reduce(
      (map, attendee) => ((map[attendee.userId] = attendee.status), map),
      Object.create(null) as Record<string, Attendee["status"]>
    ) ?? (Object.create(null) as Record<string, Attendee["status"]>);

  const votingUsers = team.teamMembers
    .filter(({ class: c }) => !c || c === "voting") // voting members only
    .map((member) => userMap[member.userId]) // get user objects
    .filter((user) => !!user); // existing user objects only

  // separate present from absent users
  const voters = {
    all: votingUsers,
    present: [] as User[],
    absent: [] as User[],
  };
  for (const voter of votingUsers) {
    if (userPresence[voter.id] && userPresence[voter.id] !== "absent") {
      voters.present.push(voter);
    } else {
      voters.absent.push(voter);
    }
  }

  return voters;
};

export const isApprovalItem = (item: AgendaItem): boolean => {
  const approverType = item.approver?.approverType;
  return !!approverType && approverType !== "none";
};

export const createNewAgendaItem = async (params: {
  agendaItem: Partial<AgendaItem>;
  tenantId: string;
  agendaModule: AgendaModule;
  meeting: Meeting;
  mutationSystem: MutationSystem;
  afterAgendaItem?: string | number;
}): Promise<AgendaItemDto> => {
  const {
    agendaItem,
    tenantId,
    agendaModule,
    meeting,
    mutationSystem,
    afterAgendaItem,
  } = params;

  const newAgendaItem = await agendaItemRepo.create(tenantId, agendaItem);

  const newAgendaItemIds = agendaModule.agendaItemIds.slice();

  if (afterAgendaItem) {
    if (typeof afterAgendaItem === "number") {
      if (afterAgendaItem < 0) {
        newAgendaItemIds.unshift(newAgendaItem.id);
      } else {
        const index = afterAgendaItem + 1;
        newAgendaItemIds.splice(index, 0, newAgendaItem.id);
      }
    } else {
      const index = agendaModule.agendaItemIds.indexOf(afterAgendaItem) + 1;
      newAgendaItemIds.splice(index, 0, newAgendaItem.id);
    }
  } else {
    newAgendaItemIds.push(newAgendaItem.id);
  }

  const agendaModuleMutationKey = "agendaModule:" + agendaModule.id;
  const meetingMutationKey = "meeting:" + meeting.id;

  await agendaModuleRepo.update(tenantId, agendaModule.id, {
    agendaItemIds: newAgendaItemIds,
  });

  agendaModule.agendaItemIds = newAgendaItemIds;

  mutationSystem.submitNewer(agendaModuleMutationKey, {
    agendaItemIds: newAgendaItemIds,
  });
  mutationSystem.submitNewer(meetingMutationKey, {});

  return newAgendaItem;
};

// build a new name by detecting a numeric suffix on the existing name,
// scanning other items in the same module for the same base name,
// finding the max suffix number and incrementing it.
const buildNextAgendaItemName = async (
  existingItem: AgendaItem,
  agendaModule: AgendaModule,
  tenantId: string
): Promise<string> => {
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // if name ends with "-<number>" treat "<number>" as a suffix
  const nameMatch = existingItem.name.match(/^(.*?)-(\d+)$/);
  const baseName = nameMatch ? nameMatch[1] : existingItem.name;

  // get other items in the module (exclude the existing item)
  const otherIds = (agendaModule.agendaItemIds ?? []).filter(
    (id) => id !== existingItem.id
  );
  const otherItems = await Promise.all(
    otherIds.map((id) => firstValueFrom(agendaItemRepo.get(tenantId, id)))
  );

  // find the highest numeric suffix for items that share the same base name
  const baseEsc = escapeRegex(baseName);
  const suffixRe = new RegExp(`^${baseEsc}-(\\d+)$`);
  let maxSuffix = -1;
  for (const it of otherItems) {
    if (!it) continue;
    if (it.name === baseName) {
      // an item with the exact base name counts as suffix 0
      maxSuffix = Math.max(maxSuffix, 0);
      continue;
    }
    const m = it.name.match(suffixRe);
    if (m) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n)) maxSuffix = Math.max(maxSuffix, n);
    }
  }

  const nextNumber = maxSuffix === -1 ? 1 : maxSuffix + 1;
  return `${baseName}-${nextNumber}`;
};

export const createVersionOfAgendaItemFromExisting = async (params: {
  existingItem: AgendaItem;
  tenantId: string;
  agendaModule: AgendaModule;
  meeting: Meeting;
  mutationSystem: MutationSystem;
}): Promise<AgendaItemDto> => {
  const { existingItem, tenantId, agendaModule, meeting, mutationSystem } =
    params;

  const newName = await buildNextAgendaItemName(
    existingItem,
    agendaModule,
    tenantId
  );
  const newItemFields: Partial<AgendaItem> = {
    name: newName,
    teamId: existingItem.teamId,
    approver: existingItem.approver,
    requestedApprovalDate: meeting.startTime,
    status: "public",
  };

  const newItem = await createNewAgendaItem({
    agendaItem: newItemFields,
    tenantId,
    agendaModule,
    meeting,
    mutationSystem,
    afterAgendaItem: existingItem.id,
  });

  return newItem;
};

export const createAgendaItemFromTemplate = async (params: {
  template: AgendaItemTemplate;
  meeting: Meeting;
  tenantId: string;
  teamId: string;
  mutationSystem: MutationSystem;
}): Promise<null | AgendaItem> => {
  const { template, meeting, teamId, tenantId, mutationSystem } = params;
  const newAgendaItemFields: Partial<AgendaItem> = {
    ...template.partialAgendaItem,
    teamId,
    approver: { approverType: "team", approverId: teamId },
    requestedApprovalDate: meeting.startTime,
    status: meeting.status,
  };
  if (!meeting.agenda?.agendaModules || !template.position) {
    return null;
  }
  const module = getModuleByAgendaItemPosition(
    meeting.agenda.agendaModules,
    template.position
  );
  if (!module) {
    return null;
  }

  let afterAgendaItem: string | number | undefined;
  if (template.position.positionType === "afterItem") {
    afterAgendaItem = template.position.positionId;
  } else if (template.position.positionType === "atSectionStart") {
    // use a negative number to indicate prepend to the module
    afterAgendaItem = -1;
  }

  const item = await createNewAgendaItem({
    agendaItem: newAgendaItemFields,
    agendaModule: module,
    meeting,
    mutationSystem,
    tenantId,
    afterAgendaItem,
  });

  return item;
};

const getModuleByAgendaItemPosition = (
  agendaModules: AgendaModule[],
  position: AgendaItemPosition
): AgendaModule | undefined => {
  if (position.positionType === "afterItem") {
    const module = findModuleByItemId(agendaModules, position.positionId);
    return module;
  }
  if (position.positionType === "atSectionStart") {
    const module = agendaModules.find(
      (module) => module.id === position.positionId
    );
    return module;
  }
  return undefined;
};

export const findModuleByItemId = (
  agendaModules: AgendaModule[],
  itemId: string
): AgendaModule | undefined => {
  return agendaModules.find((module) => module.agendaItemIds?.includes(itemId));
};

export const getStringFromPosition = (option: AgendaItemPosition): string => {
  return option.positionType + ":" + option.positionId;
};

export const parsePositionFromString = (
  value: string
): AgendaItemPosition | null => {
  const [positionType, positionId] = value.split(":");
  if (positionType && positionId) {
    return {
      positionType: positionType as "afterItem" | "atSectionStart",
      positionId,
    };
  }
  return null;
};
